import { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { SelectBox, SelectBoxItem } from '@tremor/react';
import Alert from '../campaigns/alert/Alert';
import { useUserManagement } from '../../hooks/useUserManagement';
import { validateEmails, validateNames } from '../../utils/utils';
import { toast } from 'react-hot-toast';

const UserForm = () => {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { createUser, updateUser, getUserById, clearError } =
    useUserManagement();

  // check route
  const isAddUserPage =
    location.pathname === `/admin/utenze/nuovo-utente` ? true : false;

  // Local user state
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState<'admin' | 'agent'>('agent');

  const [userNameError, setUserNameError] = useState('');
  const [userEmailError, setUserEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Carica dati utente se in modalità modifica
  useEffect(() => {
    if (!isAddUserPage && userId) {
      const loadUser = async () => {
        const result = await getUserById(userId);
        if (result.success && result.user) {
          setUserName(result.user.full_name);
          setUserEmail(result.user.email);
          setUserRole(result.user.role);
        }
      };
      loadUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isAddUserPage]); // Rimossa getUserById dalle dipendenze

  const isUserFormValidate4Create =
    userName !== '' &&
    userEmail !== '' &&
    userNameError === '' &&
    userEmailError === ''
      ? true
      : false;

  const handleSave = async () => {
    if (isLoading) {
      return false;
    }

    setIsLoading(true);
    clearError();

    try {
      if (isAddUserPage) {
        // Crea nuovo utente
        const result = await createUser({
          full_name: userName,
          email: userEmail,
          role: userRole,
        });

        if (result.success) {
          if (result.invitationLink) {
            toast.success(
              `Utente creato con successo! Email non inviata. Link di invito: ${result.invitationLink}`,
            );
          } else {
            toast.success(
              'Utente creato con successo! Email di invito inviata.',
            );
          }
          // Redirect con filtro email
          navigate('/admin/utenze?user=' + encodeURIComponent(userEmail));
        } else {
          toast.error(`Errore nella creazione utente: ${result.error}`);
          console.error('Errore nella creazione utente:', result.error);
        }
      } else {
        // Modifica utente esistente
        if (!userId) {
          throw new Error('ID utente mancante');
        }

        const result = await updateUser(userId, {
          full_name: userName,
          role: userRole,
        });

        if (result.success) {
          toast.success('Utente aggiornato con successo!');
        } else {
          toast.error(`Errore nell'aggiornamento utente: ${result.error}`);
          console.error("Errore nell'aggiornamento utente:", result.error);
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Errore sconosciuto';
      toast.error(`Errore nel salvataggio: ${errorMessage}`);
      console.error('Errore nel salvataggio:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <h3 className="user-form__title">
        {isAddUserPage ? 'Crea nuovo utente' : 'Modifica utente'}
      </h3>
      <div className={`user-form__form bg-transparent-white`}>
        <div className="row">
          <label>
            <span>*</span>Nome
          </label>
          <input
            type="text"
            value={userName}
            placeholder="Nome completo"
            onChange={(e) => setUserName(e.target.value)}
            onBlur={() => setUserNameError(validateNames(userName))}
            disabled={isLoading}
          />

          {isAddUserPage && userNameError && <Alert alertTxt={userNameError} />}
        </div>
        <div className="row">
          <label>
            <span>*</span>Email
          </label>
          <input
            type="text"
            placeholder="Email"
            onChange={(e) => setUserEmail(e.target.value)}
            onBlur={() => setUserEmailError(validateEmails(userEmail))}
            value={userEmail}
            disabled={!isAddUserPage || isLoading}
          />

          {isAddUserPage && userEmailError && (
            <Alert alertTxt={userEmailError} />
          )}
        </div>
        <div className="row">
          <label>
            <span>*</span>Ruolo
          </label>
          <SelectBox
            value={userRole}
            onValueChange={(value: string) =>
              setUserRole(value as 'admin' | 'agent')
            }
            defaultValue={userRole}
          >
            <SelectBoxItem value={'admin'} text="Admin" />
            <SelectBoxItem value={'agent'} text="Agente" />
          </SelectBox>
        </div>
        <div
          className={`cta disabled ${
            (isAddUserPage && !isUserFormValidate4Create) || isLoading
              ? 'disabled'
              : ''
          }`}
          //onClick={handleSave}
          style={{ opacity: isLoading ? 0.7 : 1 }}
        >
          {isLoading
            ? 'Salvataggio...'
            : isAddUserPage
            ? 'Crea utente'
            : 'Modifica utente'}
        </div>
        {isAddUserPage && (
          <div className="user-info-message">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
              ></path>
            </svg>
            <p>
              L'utente riceverà un'email con un link per creare la propria
              password e accedere alla piattaforma.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default UserForm;
